import React, { useState, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getColorClass_nobg, ColorText_nobg } from '../_helpers';
import apiService from '../../services/api.js';
import { Solar } from '../../services/lunar.js';
import { calculateCalendarInfo } from '../liuyao/_calendar.jsx';
import { Copy, Check } from "lucide-react";
import html2canvas from "html2canvas";
import SiteFooter from "../SiteFooter";
import QimenPublish from "./QimenPublish.jsx";

// --- Constants ---
const PALACE_NAMES = {
  1: '坎', 2: '坤', 3: '震', 4: '巽', 5: '中',
  6: '乾', 7: '兑', 8: '艮', 9: '离'
};

const GRID_LAYOUT = [4, 9, 2, 3, 5, 7, 8, 1, 6];

const BRANCH_TO_PALACE = {
  '子': 1, '丑': 8, '寅': 8, '卯': 3, '辰': 4, '巳': 4,
  '午': 9, '未': 2, '申': 2, '酉': 7, '戌': 6, '亥': 6
};

// --- Helper Functions ---
const getMaxingText = (result) => {
  if (!result?.maxing) return '无';
  const ma = Object.values(result.maxing).find(v => v);
  return ma || '无';
};

const getHourXunKong = (form) => {
  if (!form || !form.predictYear) return '';
  try {
    const solar = Solar.fromYmdHms(
      parseInt(form.predictYear),
      parseInt(form.predictMonth),
      parseInt(form.predictDay),
      parseInt(form.predictHour),
      parseInt(form.predictMinute) || 0,
      0
    );
    return solar.getLunar().getTimeXunKong();
  } catch (e) {
    console.error("Error calculating Xun Kong:", e);
    return '';
  }
};

// New Helper: Truncate
const truncateText = (text, maxLength) => {
  if (!text) return "";
  return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
};

// --- Sub-Components ---

const UserInfoSection = ({ form }) => {
  const name = (form?.name || "").toString().trim();
  const gender = (form?.gender || "").toString().trim();

  if (!name && !gender) return null;

  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden p-4 sm:p-6">
      <div className="flex flex-wrap items-center gap-4 text-sm">
        {name && <p><span className="text-gray-500">姓名：</span>{name}</p>}
        {gender && <p><span className="text-gray-500">性别：</span>{gender}</p>}
      </div>
    </div>
  );
};

const QimenHeaderSection = ({ result, hourXunKong, predictTimeText }) => (
  <>
    {/* Info Box */}
    <div className="bg-yellow-50 p-3 rounded-lg mb-4 text-sm space-y-2 border border-yellow-100">
      <div className="pb-2 border-b border-yellow-200">
        <span className="text-gray-500">预测时间：</span>
        <span className="font-medium">{predictTimeText}</span>
      </div>
      <div className="flex flex-wrap gap-4 pt-2 border-yellow-200">
        
        <div><span className="text-gray-500">局数：</span><span className="font-medium">{result.dunju}</span></div>
        <div><span className="text-gray-500">值符：</span><span className="font-medium">{result.zhifu_star} (落{result.zhifu_start_palace}宫)</span></div>
        <div><span className="text-gray-500">值使：</span><span className="font-medium">{result.zhishi_men} (落{result.zhishi_men_place}宫)</span></div>
         <div className="text-gray-500 font-medium">旬首：{result.xunshou}</div>
        <div><span className="text-gray-500">马星：</span><span className="font-medium">{getMaxingText(result)}</span></div>
     
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div><span className="text-gray-500">上节气：</span>{result.pre_jieqi} ({result.pre_jieqi_day})</div>
        <div><span className="text-gray-500">下节气：</span>{result.next_jieqi} ({result.next_jieqi_day})</div>
      </div>
    </div>

    {/* Pillars */}
    <div className="text-center mb-6">
      <div className="text-lg sm:text-xl font-bold text-gray-800 tracking-wide flex justify-center gap-4">
        <span><ColorText_nobg text={result.year_pillar} />年</span>
        <span><ColorText_nobg text={result.month_pillar} />月</span>
        <span><ColorText_nobg text={result.day_pillar} />日</span>
        <span><ColorText_nobg text={result.hour_pillar} />时</span>
      </div>
      <div className="mt-2 text-sm text-gray-500">
        (时空：<span className="font-medium text-gray-700">{hourXunKong}</span>)
      </div>
    </div>
  </>
);

const PalaceCell = ({ palaceId, data, hourXunKong }) => {
  const {
    dipan_gan: di = '',
    tianpan_gan: tian = '',
    men = '',
    xing = '',
    shen = '',
    an_gan: an = '',
    maxing: ma = '',
  } = data || {};

  const palaceName = PALACE_NAMES[palaceId] || '';

  // Xun Kong Logic
  const xunKongBranches = hourXunKong ? hourXunKong.split('') : [];
  const currentPalaceBranches = xunKongBranches.filter(b => BRANCH_TO_PALACE[b] === palaceId);

  return (
    <div className="relative border border-gray-300 bg-white p-1 flex flex-col justify-between aspect-square">
      {/* 左上角：旬空 */}
      {currentPalaceBranches.length > 0 && (
        <div className="absolute top-0 left-0 p-0.5 text-[10px] sm:text-xs font-bold z-10">
            {currentPalaceBranches.map(b => <span key={b} className="font-bold sm:text-xs animate-pulse block sm:inline">空:{b}</span>)}
        </div>
      )}

      {/* 顶部：神 */}
      <div className={`text-center text-xs sm:text-sm font-bold mt-1 ${getColorClass_nobg(shen) || 'text-red-600'}`}>{shen}</div>
      
      {/* 中部：星、门 */}
      <div className="flex justify-between items-center my-auto px-1">
        <div className={`text-xs sm:text-sm font-medium ${getColorClass_nobg(xing) || 'text-blue-600'}`}>{xing}</div>
        <div className={`text-xs sm:text-sm font-medium ${getColorClass_nobg(men) || 'text-green-600'}`}>{men}</div>
      </div>

      {/* 底部：天盘、地盘、暗干 */}
      <div className="flex justify-between items-end text-xs sm:text-sm mb-0.5">
         {/* 左下：暗干 */}
         <div className="text-gray-400 scale-90 origin-bottom-left w-4">
         {an}
         </div>
         
         {/* 中间：天盘/地盘 */}
         <div className="flex flex-col items-center leading-tight">
           <span className="font-bold text-black">
             {tian && <ColorText_nobg text={tian.replace(/\(.*\)/, '')} />}
           </span>
           <span className="font-bold text-gray-600">
             <ColorText_nobg text={di} />
           </span>
         </div>
         
         {/* 右下：宫名/马星 */}
         <div className="flex flex-col items-end w-auto translate-y-2">
            {ma && <span className="text-[10px] font-bold animate-pulse text-indigo-500 scale-90 origin-bottom-right mb-0.5">马:{ma}</span>}
            <span className={`scale-90 origin-bottom-right text-gray-400 whitespace-nowrap`}>
              {palaceName}{palaceId}
            </span>
         </div>
      </div>
      
      {/* 寄宫显示 (如果天盘有括号内容) */}
      {tian && tian.includes('(') && (
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-[10px] text-gray-400 opacity-50 pointer-events-none">
              <ColorText_nobg text={tian.match(/\((.*)\)/)[1]} />
          </div>
      )}
    </div>
  );
};

const QimenGrid = ({ result, hourXunKong }) => (
  <div className="max-w-[360px] mx-auto aspect-square"> 
    <div className="grid grid-cols-3 gap-0.5 bg-gray-300 border border-gray-300 p-0.5 h-full w-full">
        {GRID_LAYOUT.map(id => (
        <PalaceCell
            key={id}
            palaceId={id}
            data={result.palaces?.[id]}
            hourXunKong={hourXunKong}
        />
        ))}
    </div>
  </div>
);

const TabsPanel = ({ result, feedback, setFeedback, analysis, setAnalysis, onBack, onSave, onPublish }) => {
  const [activeTab, setActiveTab] = useState("diagnosis");

  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden p-4 sm:p-6 h-full">
      <div className="flex gap-2 border-b border-gray-100 pb-3 mb-4">
        <button
          onClick={() => setActiveTab("diagnosis")}
          className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
            activeTab === "diagnosis"
              ? "bg-indigo-600 text-white"
              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
          }`}
        >
          奇门诊断
        </button>
        <button
          onClick={() => setActiveTab("feedback")}
          className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
            activeTab === "feedback"
              ? "bg-indigo-600 text-white"
              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
          }`}
        >
          命主反馈
        </button>
        <button
          onClick={() => setActiveTab("analysis")}
          className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
            activeTab === "analysis"
              ? "bg-indigo-600 text-white"
              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
          }`}
        >
          断语分析
        </button>
      </div>

      {activeTab === "diagnosis" && (
        <div className="space-y-4 text-sm">
          <div className="bg-purple-50 p-3 rounded-lg border border-purple-100">
            <h4 className="font-bold text-purple-800 mb-2">局势分析</h4>
            <div className="space-y-1 text-gray-700">
              <p><span className="text-gray-500">遁局：</span>{result.dunju}</p>
              <p><span className="text-gray-500">旬首：</span>{result.xunshou}</p>
              <p><span className="text-gray-500">空亡：</span>{result.xun_kong || '未计算'}</p>
              <p><span className="text-gray-500">马星：</span>{getMaxingText(result)}</p>
            </div>
          </div>

          <div className="bg-indigo-50 p-3 rounded-lg border border-indigo-100">
            <h4 className="font-bold text-indigo-800 mb-2">关键符号</h4>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>值符星</span>
                <span className="font-medium">{result.zhifu_star} (落{result.zhifu_start_palace}宫)</span>
              </div>
              <div className="flex justify-between">
                <span>值使门</span>
                <span className="font-medium">{result.zhishi_men} (落{result.zhishi_men_place}宫)</span>
              </div>
            </div>
          </div>

          <div className="border-t pt-4">
            <p className="text-gray-500 italic text-xs">此处可扩展更多自动诊断逻辑，如吉凶格局判断、克应关系分析等。</p>
          </div>
        </div>
      )}

      {activeTab === "feedback" && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">命主反馈</label>
          <textarea
            className="w-full p-3 bg-gray-50 border border-gray-200 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            rows="6"
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder="在此记录命主的反馈信息..."
          />
        </div>
      )}

      {activeTab === "analysis" && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">断语 / 分析</label>
          <textarea
            className="w-full p-3 bg-gray-50 border border-gray-200 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            rows="6"
            value={analysis}
            onChange={(e) => setAnalysis(e.target.value)}
            placeholder="在此记录您的断语分析..."
          />
        </div>
      )}

      <div className="mt-6 flex justify-center gap-4">
        <button
          onClick={onBack}
          className="px-6 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 shadow-sm min-w-[100px]"
        >
          返回
        </button>
        <button
          onClick={onSave}
          className="px-6 py-2 bg-indigo-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-indigo-700 shadow-sm min-w-[100px]"
        >
          保存卦例
        </button>
        <button
          onClick={onPublish}
          className="px-6 py-2 bg-green-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-green-700 shadow-sm min-w-[100px]"
        >
          发   布
        </button>
      </div>
    </div>
  );
};

const ActionButtons = ({ onBack, onSave }) => (
  <div className="mt-6 flex justify-center gap-4">
    <button
      onClick={onBack}
      className="px-6 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 shadow-sm"
    >
      返回重新排盘
    </button>
    <button
      onClick={onSave}
      className="px-6 py-2 bg-indigo-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-indigo-700 shadow-sm"
    >
      保存
    </button>
  </div>
);

const PillarBox = ({ pillar }) => {
  if (!pillar || typeof pillar !== 'string' || pillar.length < 2) return <span>{pillar}</span>;
  const gan = pillar[0];
  const zhi = pillar[1];
  // Reuse basic color helper if available, or simple maps
  const getColor = (char) => { /* simple mapping */ };
  return (
    <div className="flex flex-col items-center border border-gray-300 rounded px-2 py-1 bg-gray-50">
       <span className={`font-bold ${getColor(gan)}`}>{gan}</span>
       <span className={`font-bold ${getColor(zhi)}`}>{zhi}</span>
    </div>
  );
}

// --- Main Component ---

const QimenDetail = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const locationState = location.state || {};

  const [result, setResult] = useState(locationState.result || null);
  const [form, setForm] = useState(locationState.form || null);
  const [feedback, setFeedback] = useState(locationState.feedback || '');
  const [analysis, setAnalysis] = useState(locationState.analysis || '');
  const [copied, setCopied] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const chartRef = useRef(null); // Ref for capturing image

  // Calculate Birth Pillar (Memoized)
  const birthYearPillar = useMemo(() => {
    if (!form || !form.birthYear) return null;
    try {
       const pad = (n) => String(n).padStart(2, '0');
       const dateStr = `${form.birthYear}${pad(form.birthMonth || 1)}${pad(form.birthDay || 1)}${pad(form.birthHour || 0)}0000`;
       const cal = calculateCalendarInfo(dateStr, true);
       return cal?.ganzhi?.year || null;
    } catch(e) { return null; }
  }, [form]);

  const handleCopy = async () => {
    if (!chartRef.current) return;
    
    // Optimistic UI update
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);

    try {
      // Create a temporary container for layout
      const container = document.createElement("div");
      container.style.position = "absolute";
      container.style.top = "-9999px";
      container.style.left = "-9999px";
      const mobileWidth = 430; 
      container.style.width = `${mobileWidth}px`;
      container.style.backgroundColor = "#ffffff"; 
      container.style.display = "flex";
      container.style.flexDirection = "column"; 
      container.style.fontFamily = getComputedStyle(document.body).fontFamily;
      
      // Clone nodes
      const chartClone = chartRef.current.cloneNode(true);
      
      // Clean up styles for the image
      chartClone.style.boxShadow = "none";
      chartClone.style.border = "none";
      chartClone.style.margin = "0";
      chartClone.style.width = "100%";
      chartClone.classList.remove("shadow-md", "rounded-xl", "lg:w-3/5"); 
      
      // Remove buttons from clone
      const buttons = chartClone.querySelectorAll("button");
      buttons.forEach(btn => btn.style.display = "none");

      container.appendChild(chartClone);
      document.body.appendChild(container);

      // Capture
      const scale = 3;
      const canvas = await html2canvas(container, {
        useCORS: true,
        scale: scale, 
        backgroundColor: "#ffffff"
      });
      
      document.body.removeChild(container);
      
      // Add footer
      const footerHeight = 40;
      const scaledFooterHeight = footerHeight * scale; 
      
      const finalCanvas = document.createElement("canvas");
      finalCanvas.width = canvas.width;
      finalCanvas.height = canvas.height + scaledFooterHeight;
      const ctx = finalCanvas.getContext("2d");
      
      // Fill BG
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, finalCanvas.width, finalCanvas.height);
      
      // Draw content
      ctx.drawImage(canvas, 0, 0);
      
      // Draw Footer Text
      const fontSize = 12 * scale;
      ctx.font = `${fontSize}px sans-serif`; 
      ctx.fillStyle = "#9ca3af"; 
      ctx.textAlign = "center";
      
      ctx.fillText("圆運命理系统", finalCanvas.width / 2, canvas.height + 20 * scale);
      ctx.fillText("bazi.tryonebest.com", finalCanvas.width / 2, canvas.height + 40 * scale);
      
      // Copy to clipboard
      finalCanvas.toBlob(async (blob) => {
          if (!blob) return;
          try {
              await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
          } catch(e) {
              console.error(e);
              // Fallback
              const link = document.createElement('a');
              link.href = finalCanvas.toDataURL("image/png");
              link.download = `qimen_result_${Date.now()}.png`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              alert("自动复制失败，已为您下载图片。");
          }
      }, "image/png");

    } catch (e) {
      console.error(e);
      alert("生成图片失败");
    }
  };

  

  const handleSave = async () => {
    if (!apiService.isAuthenticated()) {
      const returnInfo = {
        path: location.pathname,
        search: location.search,
        state: { result, form, feedback, analysis }
      };
      sessionStorage.setItem("post_login_return", JSON.stringify(returnInfo));
      alert('请先登录，登录后将自动返回当前页面');
      navigate('/login');
      return;
    }

    const data = {
      form,
      result,
      feedback,
      analysis,
      chart_id: result?.qimenchart_id || result?.id
    };

    try {
      const res = await apiService.saveQimenChart(data);
      if (res.success) {
        // 优先使用 state 中的 ID，其次是 fullChart 中的 ID
        const chartId = result?.qimenchart_id || result?.id || null;
        alert(chartId ? "更新成功" : "保存成功");
        if (res.id) {
          setResult(prev => ({ ...prev, qimenchart_id: res.id }));
        }
      } else {
        alert('保存失败: ' + res.error);
      }
    } catch (error) {
       alert('保存时发生错误');
    }
  };

  // 发布按钮点击，先保存后弹窗
  const handlePublish = async () => {
    if (!apiService.isAuthenticated()) {
      const returnInfo = {
        path: location.pathname,
        search: location.search,
        state: { result, form, feedback, analysis }
      };
      sessionStorage.setItem("post_login_return", JSON.stringify(returnInfo));
      alert('请先登录，登录后将自动返回当前页面');
      navigate('/login');
      return;
    }

    if (!result) {
      alert("无奇门结果可保存");
      return;
    }

    // 先保存，确保有chart_id
    const data = {
      form,
      result,
      feedback,
      analysis,
      chart_id: result?.qimenchart_id || result?.id
    };

    try {
      const res = await apiService.saveQimenChart(data);
      if (res.success) {
        // 保存成功后更新 id，再打开发布弹窗
        if (res.id) {
          setResult(prev => ({ ...prev, qimenchart_id: res.id }));
        }
        setShowPublishModal(true);
      } else {
        alert("保存失败: " + res.error);
      }
    } catch (error) {
      alert("保存失败: " + (error.message || "未知错误"));
    }
  };

  // 发布弹窗提交
  const handlePublishSubmit = async (formData) => {
    // formData: { chartId, publishType, ... }
    try {
      const res = await apiService.publishQimenChart(formData);
      if (res.success) {
        
        alert('发布成功！');
        setShowPublishModal(false);
      } else {
        alert('发布失败: ' + (res.error || '未知错误'));
      }
    } catch (error) {
      alert('发布失败: ' + (error?.message || '未知错误'));
    }
  };

  if (!result || !form) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <p className="text-xl text-gray-600 mb-4">暂无排盘数据</p>
          <button
            onClick={() => navigate('/qimen')}
            className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
          >
            返回排盘页
          </button>
        </div>
      </div>
    );
  }

  const hourXunKong = getHourXunKong(form);
  const predictTimeText = `${form.predictYear}年${form.predictMonth}月${form.predictDay}日 ${form.predictHour}时`;

  return (
    <div className="py-6 px-2 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto space-y-6">
        <UserInfoSection form={form} />
        
        <div className="flex flex-col lg:flex-row gap-6 items-start">
            {/* Chart Panel */}
            <div ref={chartRef} className="w-full lg:w-3/5 bg-white rounded-xl shadow-md overflow-hidden p-4 sm:p-6">
              {/* Header */}
              <div className="flex justify-between items-center border-b pb-2 mb-4">
                 <h3 className="text-lg font-bold text-gray-900">
                   {form.type} <span className="mx-2 text-gray-300">|</span> {truncateText(form.content, 20)}
                 </h3>
                 {birthYearPillar && (
                   <div className="text-sm font-medium text-gray-600 bg-gray-50 px-2 py-1 rounded border border-gray-200">
                     本命: <span className="text-indigo-600 font-bold">{birthYearPillar}</span>
                   </div>
                 )}
              </div>
              <QimenHeaderSection result={result} hourXunKong={hourXunKong} predictTimeText={predictTimeText} />
              <QimenGrid result={result} hourXunKong={hourXunKong} />

              {/* Copy Button */}
              <div className="flex justify-end mt-4 pt-2 border-t border-gray-100">
                 <button
                    onClick={handleCopy}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-500 hover:text-indigo-600 bg-gray-50 hover:bg-indigo-50 rounded-md transition-colors border border-transparent hover:border-indigo-100"
                    title="复制排盘结果为图片"
                  >
                    {copied ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                    {copied ? "已复制到剪贴板" : "复制为图片"}
                 </button>
              </div>
           </div>

           {/* Tabs Panel */}
           <div className="w-full lg:w-2/5 flex-shrink-0">
              <TabsPanel
                result={result}
                feedback={feedback}
                setFeedback={setFeedback}
                analysis={analysis}
                setAnalysis={setAnalysis}
                onBack={() => navigate('/qimen/index')}
                onSave={handleSave}
                onPublish={handlePublish}
              />
           </div>
        </div>

        {/* 发布弹窗 */}
        <QimenPublish
          isOpen={showPublishModal}
          onClose={() => setShowPublishModal(false)}
          onSubmit={handlePublishSubmit}
          chartData={{
            id: result?.qimenchart_id || result?.id,
            content: form?.content,
            type: form?.type,
            name: form?.name
          }}
        />
      </div>
      <SiteFooter />
    </div>
  );
};

export default QimenDetail;
